/**
 * The inline ＋ beside the Client / Client contact / Project pickers — the
 * prototype's `newLookup` quick-add.
 *
 * The point of it: a picker narrowed to the selected client (design §10.1) will
 * often be EMPTY, and "no contacts on file" is an honest answer rather than a
 * dead end — the ＋ is right there to create one without leaving the request.
 *
 * All three kinds POST to the EXISTING Clients master-data endpoints
 * (`/api/v1/clients` · `/{id}/contacts` · `/{id}/projects`), so a record created
 * here is identical to one created from the Clients form and is audited by that
 * module exactly as it would be there — no second create path for the same
 * table (standing rule 8).
 *
 * <p>CLIENT (added 2026-09-16, prototype v68): `dbo.Customer` had no create
 * endpoint at all, so one was added to the Clients module rather than here. Two
 * of its five fields do not live where the prototype implies:</p>
 *
 * <ul>
 *   <li><b>Account code</b> is not a column on the client — it becomes a
 *       `dbo.ClientAccountNumber` row filed under it (owner decision).</li>
 *   <li><b>Payment terms</b> come from Settings → Accounting → Payment Terms,
 *       where a term is a number of DAYS. The prototype's hardcoded
 *       Net 15/30/45/Prepaid list is not used: "Prepaid" is not a day count,
 *       and inventing options the lab cannot then edit would be worse than
 *       showing the real ones.</li>
 * </ul>
 */
import { useEffect, useState } from 'react';
import { AppDialog } from '../../components/AppDialog';
import { BusyIcon } from '../../components/loading';
import { useNotify } from '../../components/notifications';
import {
  createClient, createContact, createProject, fetchPaymentTerms,
  type PaymentTermOption,
} from './clientRequestApi';

export type QuickAddKind = 'client' | 'contact' | 'project';

interface Props {
  kind: QuickAddKind | null;
  /** The client a contact/project is filed under — always set for those two,
   *  because their ＋ is disabled until a client is chosen. Unused for 'client',
   *  which has no parent. */
  clientId:   string;
  clientName: string;
  /** Existing client names, so a near-duplicate can be pointed out before it is
   *  created. NOT a block: this database already holds five clients called
   *  "City of Houston", so refusing a repeat would refuse reality. */
  clientNames?: string[];
  onClose: () => void;
  /** The new record's Oid and display label, so the caller can select it
   *  immediately — creating something and then making the user find it in the
   *  list would defeat the point. */
  onCreated: (id: string, label: string) => void;
}

const EMPTY = {
  first: '', last: '', email: '', phone: '', role: '',
  code: '', name: '', site: '', terms: '',
};

const TITLE: Record<QuickAddKind, string> = {
  client: 'New Client', contact: 'New Contact', project: 'New Project',
};
const ICON: Record<QuickAddKind, string> = {
  client: 'ti-building-plus', contact: 'ti-user-plus', project: 'ti-folder-plus',
};

export default function QuickAddDialog(
  { kind, clientId, clientName, clientNames = [], onClose, onCreated }: Props,
) {
  const [f, setF]           = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [tried,  setTried]  = useState(false);
  const [terms,  setTerms]  = useState<PaymentTermOption[]>([]);
  const notify = useNotify();

  // Fresh fields each time the dialog opens, so a cancelled entry never leaks
  // into the next one.
  useEffect(() => { if (kind) { setF(EMPTY); setTried(false); } }, [kind]);

  // The terms list is the lab's own (Settings → Accounting). Loaded only for the
  // client dialog, and a failure leaves the dropdown empty rather than blocking
  // a client whose terms nobody was going to set on the telephone anyway.
  useEffect(() => {
    if (kind !== 'client') return;
    let alive = true;
    void (async () => {
      try {
        const rows = await fetchPaymentTerms();
        if (alive) setTerms(rows);
      } catch {
        if (alive) setTerms([]);
      }
    })();
    return () => { alive = false; };
  }, [kind]);

  const set = (k: keyof typeof EMPTY, v: string) => setF(s => ({ ...s, [k]: v }));

  // Mirrors the server's own required field(s) for each endpoint, so the dialog
  // never lets through a payload the API will reject (§14b.5 — the server stays
  // authoritative; this is the UX half).
  //
  // NAMED rather than merely counted, so the message and the ringed field say
  // the same thing. Add on an empty form used to tint a border and otherwise do
  // nothing at all, which reads as a broken button rather than as a refusal —
  // the same complaint that put `refuseSave` on the request form.
  const missingLabels = kind === 'client'
    ? (!f.name.trim() ? ['Client name'] : [])
    : kind === 'contact'
      ? [...(!f.first.trim() ? ['First name'] : []), ...(!f.email.trim() ? ['Email'] : [])]
      : kind === 'project'
        ? (!f.code.trim() ? ['Project number'] : [])
        : [];
  const missing = missingLabels.length > 0;

  /** A client by this name already exists — worth saying, never worth blocking. */
  const duplicate = kind === 'client' && f.name.trim().length > 0
    && clientNames.some(n => n.trim().toLowerCase() === f.name.trim().toLowerCase());

  const save = async () => {
    setTried(true);
    if (missing || !kind) return;
    setSaving(true);
    try {
      if (kind === 'client') {
        const label = f.name.trim();
        const id = await createClient({
          customerName:  label,
          accountNumber: f.code.trim()  || undefined,
          officePhone:   f.phone.trim() || undefined,
          email:         f.email.trim() || undefined,
          termsId:       f.terms || undefined,
        });
        notify.success(`${label} added and selected.`);
        onCreated(id, label);
      } else if (kind === 'contact') {
        const id = await createContact(clientId, {
          firstName:   f.first.trim(),
          lastName:    f.last.trim() || undefined,
          email:       f.email.trim(),
          officePhone: f.phone.trim() || undefined,
          position:    f.role.trim() || undefined,
        });
        const label = `${f.first.trim()} ${f.last.trim()}`.trim();
        notify.success(`${label} added and selected.`);
        onCreated(id, label);
      } else {
        const id = await createProject(clientId, {
          projectId:       f.code.trim(),
          projectName:     f.name.trim() || undefined,
          projectLocation: f.site.trim() || undefined,
        });
        const label = f.name.trim() || f.code.trim();
        notify.success(`${label} added and selected.`);
        onCreated(id, label);
      }
      onClose();
    } catch (e) {
      notify.error(e instanceof Error
        ? `Could not add the ${kind}: ${e.message}`
        : `Could not add the ${kind}.`);
    } finally {
      setSaving(false);
    }
  };

  const cls = (empty: boolean) => `sd-input${tried && empty ? ' required-empty' : ''}`;

  return (
    <AppDialog
      open={kind !== null}
      title={kind ? TITLE[kind] : ''}
      icon={kind ? ICON[kind] : undefined}
      width={560}
      onClose={onClose}
      footer={
        <>
          <button className="ca-btn ca-btn-solid" onClick={save} disabled={saving}>
            {saving ? <BusyIcon busy icon="ti-device-floppy" /> : <span className="tb-i"><i className="ti ti-plus" /></span>} Add
          </button>
          <button className="ca-btn" onClick={onClose} disabled={saving}>
            <span className="tb-i"><i className="ti ti-x" /></span> Cancel
          </button>
        </>
      }
    >
      {/* AI STANDARD FORM LAYOUT (standing rule 12) — the SHARED grid from
          SettingsDetailPage.css, nothing re-implemented here (rule 8):
          `sd-grid--side` puts each caption BESIDE its control in a fixed-width,
          LEFT-aligned 150px column with the ":" suffix, every control a uniform
          26px, a 6px row gap, and the yellow editable / grey read-only cue —
          measured against the reference form, Settings > Inventory Management >
          Items. These three popups were the last part of Request Registration
          still stacking the label ABOVE its field (owner report 2026-10-09); the
          form body behind them already uses `sd-grid--side cr-side`.

          `sd-grid--single` (ONE pair per row) rather than Items' two columns: a
          150px label column twice over inside a dialog leaves about 85px for each
          field. Same choice, same reason, as the ConditionalFormatDialog
          precedent. No `cr-*` class is needed — every caption here is one or two
          short words, so the shared 150px column fits them without the widening
          `.cr-side` has to do for the sentence-length captions on the form
          behind. */}
      <div className="sd-grid sd-grid--single sd-grid--side">
        {/* A contact/project is filed under the client on the request — nobody
            should retype it. A client has no parent, so the row is omitted. */}
        {kind !== 'client' && (
          <div className="sd-row">
            <label className="sd-label">Client</label>
            <input className="sd-input readonly" value={clientName} readOnly disabled />
          </div>
        )}

        {kind === 'client' && (
          <>
            <div className="sd-row">
              <label className="sd-label">Client name<span className="req">*</span></label>
              <input className={cls(!f.name.trim())} value={f.name}
                     placeholder="Legal or trading name"
                     onChange={e => set('name', e.target.value)} />
            </div>
            <div className="sd-row">
              <label className="sd-label">Account code</label>
              <input className="sd-input" value={f.code} placeholder="e.g. NORTH-01"
                     onChange={e => set('code', e.target.value)} />
            </div>
            <div className="sd-row">
              <label className="sd-label">Main phone</label>
              <input className="sd-input" value={f.phone} placeholder="(713) 555-0100"
                     onChange={e => set('phone', e.target.value)} />
            </div>
            <div className="sd-row">
              <label className="sd-label">Billing email</label>
              <input className="sd-input" value={f.email} type="email" placeholder="ap@client.com"
                     onChange={e => set('email', e.target.value)} />
            </div>
            <div className="sd-row">
              <label className="sd-label">Payment terms</label>
              <select className="sd-input" value={f.terms}
                      onChange={e => set('terms', e.target.value)}>
                <option value="">— select —</option>
                {/* "Net 30", the prototype's wording (owner decision
                    2026-09-16) — built from dbo.PaymentTerms.Terms, which holds
                    the day count itself. */}
                {terms.map(t => (
                  <option key={t.id} value={t.id}>{`Net ${t.days}`}</option>
                ))}
              </select>
            </div>
          </>
        )}

        {kind === 'contact' && (
          <>
            <div className="sd-row">
              <label className="sd-label">First name<span className="req">*</span></label>
              <input className={cls(!f.first.trim())} value={f.first}
                     onChange={e => set('first', e.target.value)} />
            </div>
            <div className="sd-row">
              <label className="sd-label">Last name</label>
              <input className="sd-input" value={f.last}
                     onChange={e => set('last', e.target.value)} />
            </div>
            <div className="sd-row">
              <label className="sd-label">Email<span className="req">*</span></label>
              <input className={cls(!f.email.trim())} value={f.email} type="email"
                     onChange={e => set('email', e.target.value)} />
            </div>
            <div className="sd-row">
              <label className="sd-label">Phone</label>
              <input className="sd-input" value={f.phone}
                     onChange={e => set('phone', e.target.value)} />
            </div>
            <div className="sd-row">
              <label className="sd-label">Role</label>
              <input className="sd-input" value={f.role} placeholder="e.g. Project Manager"
                     onChange={e => set('role', e.target.value)} />
            </div>
          </>
        )}

        {kind === 'project' && (
          <>
            <div className="sd-row">
              <label className="sd-label">Project number<span className="req">*</span></label>
              <input className={cls(!f.code.trim())} value={f.code} placeholder="PRJ-2026-000"
                     onChange={e => set('code', e.target.value)} />
            </div>
            <div className="sd-row">
              <label className="sd-label">Project name</label>
              <input className="sd-input" value={f.name} placeholder="What the client calls it"
                     onChange={e => set('name', e.target.value)} />
            </div>
            <div className="sd-row">
              <label className="sd-label">Site address</label>
              <input className="sd-input" value={f.site} placeholder="Street, city, state"
                     onChange={e => set('site', e.target.value)} />
            </div>
          </>
        )}
      </div>

      {/* The refusal, in words. Inline rather than a toast or a second dialog:
          field-level validation belongs beside the fields (§18.6 Tier 3), and a
          modal over a modal would bury the form being corrected. */}
      {tried && missing && (
        <div className="sd-error" style={{ marginTop: 10 }} role="alert">
          <i className="ti ti-alert-triangle" />
          {missingLabels.length === 1
            ? `${missingLabels[0]} is required.`
            : `${missingLabels.join(' and ')} are required.`}
        </div>
      )}

      {/* Said, not enforced — the lab genuinely has several clients sharing a
          name, so this is information for the person typing, not a refusal. */}
      {duplicate && (
        <div className="dhint" style={{ marginTop: 14 }}>
          <i className="ti ti-alert-triangle" />
          A client called “{f.name.trim()}” already exists. Adding another is allowed —
          check the list first if you meant that one.
        </div>
      )}
    </AppDialog>
  );
}
